import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PostAlbumVideoCommentsResult,
  PostAlbumVideoCommentsOptions,
  GetAlbumVideoCommentsOptions,
  GetAlbumVideoCommentsResult,
  getAlbumVideoComments,
  PostAlbumVideoCommentsFields,
  postAlbumVideoComments,
  GetAlbumVideoCommentsFields,
} from '../fetchers/album/video/comments';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetAlbumVideoCommentsOptions<Fields extends GetAlbumVideoCommentsFields> =
  GetAlbumVideoCommentsOptions<Fields>;

/**
 * Get all the video comments on a video
 * This method returns every video comment on the specified video.
 */

export function useGetAlbumVideoComments<Fields extends GetAlbumVideoCommentsFields>(
  getOptions:
    | UseGetAlbumVideoCommentsOptions<Fields>
    | (() => UseGetAlbumVideoCommentsOptions<Fields> | null),
  config?: SWRConfiguration<GetAlbumVideoCommentsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/albums/${options.where.albumId}/videos/${options.where.videoId}/comments${serializeQuery(
          options,
        )}`
      : () => null,
    options
      ? () => {
          return getAlbumVideoComments({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetAlbumVideoComments, {
    endpoint: '/albums/:albumId/videos/:videoId/comments',
    method: 'GET',
  });
}

/**
 * Get all the video comments on a video
 * This method returns every video comment on the specified video.
 */

export function useGetAlbumVideoCommentsLazy<Fields extends GetAlbumVideoCommentsFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetAlbumVideoCommentsResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetAlbumVideoCommentsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/albums/${options.where.albumId}/videos/${
            options.where.videoId
          }/comments${serializeQuery(options)}`,
          getAlbumVideoComments({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetAlbumVideoCommentsLazy, {
    endpoint: '/albums/:albumId/videos/:videoId/comments',
    method: 'GET',
  });
}

type UseGetAlbumVideoCommentsOptionsInfinite<Fields extends GetAlbumVideoCommentsFields> =
  GetAlbumVideoCommentsOptions<Fields>;

/**
 * Get all the video comments on a video
 * This method returns every video comment on the specified video.
 */

export function useGetAlbumVideoCommentsInfinite<Fields extends GetAlbumVideoCommentsFields>(
  getOptions:
    | UseGetAlbumVideoCommentsOptionsInfinite<Fields>
    | (() => UseGetAlbumVideoCommentsOptionsInfinite<Fields> | null),
  config?: SWRInfiniteConfiguration<GetAlbumVideoCommentsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let getKey = (
    pageIndex: number,
    previousPageData: GetAlbumVideoCommentsResult<Fields> | null,
  ) => {
    if (options === null) {
      return null;
    }
    if (previousPageData && !previousPageData.paging.next) {
      return null;
    }
    let { perPage = 25, page: _page, ...rest } = options.query ?? {};
    let select = options.select.join(',');
    let queryString = Object.entries(rest ?? {})
      .filter(([, value]) => typeof value !== 'undefined')
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return [
      `/albums/${options.where.albumId}/videos/${options.where.videoId}/comments?page=${
        pageIndex + 1
      }&perPage=${perPage}&fields=${select}&${queryString}`,
      pageIndex,
    ];
  };
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWRInfinite(
    getKey,
    options !== null
      ? ([_uri, pageIndex]: [string, number]) => {
          return getAlbumVideoComments({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            query: { ...options.query, page: pageIndex + 1 },
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetAlbumVideoCommentsInfinite, {
    endpoint: '/albums/:albumId/videos/:videoId/comments',
    method: 'GET',
  });
}

type UsePostAlbumVideoCommentsOptions<Fields extends PostAlbumVideoCommentsFields> =
  PostAlbumVideoCommentsOptions<Fields>;

/**
 * Add a video comment to a video
 * This method adds a video comment to the specified video.
 */

export function usePostAlbumVideoComments<Fields extends PostAlbumVideoCommentsFields>() {
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PostAlbumVideoCommentsResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePostAlbumVideoCommentsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await postAlbumVideoComments({
          ...options,
          baseUrl,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
            Authorization: jwt ? `jwt ${jwt}` : '',
            'Vimeo-Page': `${xVimeoPage}`,
            'Accept-Language': locale ?? 'en',
          },
        });
        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePostAlbumVideoComments, {
    endpoint: '/albums/:albumId/videos/:videoId/comments',
    method: 'POST',
  });
}
